import type { Enemy } from "../entities/Enemy.ts";
import { breathe, flicker, snap, settle } from "./AnimationUtils.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { canvasFont } from "./canvasType.ts";
import { COLORS } from "../core/constants.ts";
import { ENEMY_PALETTE } from "../core/palette.ts";
import { buildPhantom } from "./EnemyShapes.ts";
import { deriveRamp, emissiveFill } from "./EmissiveMaterial.ts";
import type { Ramp } from "./EmissiveMaterial.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

/**
 * StealthRenderer (BL-49 — owner Prunkstück). The Stealth Daemon was the one
 * entity still on the universal flat diamond; this pass elevates it to the hero
 * showcase:
 *
 *   - a bespoke **phantom silhouette** (`buildPhantom`), lit by the shared S79
 *     emissive material so it's a lit volume like every other entity;
 *   - a **cloak** (undetected) = a deep #C42DBE phantom kept hard-to-focus
 *     (alpha-flicker dropout + warm/cool chromatic fringe + glitch edge) wrapped
 *     in a Canvas2D **heat-haze distortion** (displaced shimmer copies + a
 *     wobbling refraction ring) so "something's there" is unmistakable;
 *   - a **reveal** (detected) = an explosive **white-hot phase-in** driven by
 *     `snap` (scale overshoot) + `settle` (white-hot blowout that feeds the
 *     existing WebGL bloom), settling into a sharp, hot EXPOSED phantom under a
 *     red system-alarm (pulse rings + cycling BREACH labels + binary debris).
 *
 * This module is **render-only**: it never writes sim state. The reveal/cloak
 * *mechanics* (StealthGuard / BeamCombat / `enemy.revealed*`) are untouched, and
 * the heat-haze is a player-facing tell only — hidden stealth stays
 * untargetable. The WebGL true-refraction pass (warp what's *behind* the cloak)
 * is filed as the shared BL-49/BL-50 follow-up; this ships the Canvas2D floor.
 */

/** Duration (seconds) of the reveal/cloak transition phase-in. */
const TRANSITION_DURATION = 0.5;

/** Micro-label strings that cycle above exposed enemies. */
const BREACH_LABELS = ["BREACH", "EXPOSED", "ALERT", "LOCKED"];

/** Binary debris characters for the revealed-state particle trail. */
const DEBRIS_CHARS = ["0", "1", "0", "1", "{", "}", "<", ">"];

/** The Stealth Daemon's assigned threat colour (central palette — do not re-pick). */
const STEALTH_MAGENTA = ENEMY_PALETTE.stealth_daemon!;

/** Cloak emissive ramp = the assigned hue, derived like every other enemy. */
const CLOAK_RAMP = deriveRamp(STEALTH_MAGENTA);

/** Exposed emissive ramp = the same hue *energised* (hotter core + brighter
 *  mid) for the post-reveal "dangerous / hot" read. Same palette identity. */
const EXPOSED_RAMP: Ramp = {
  core: "#FFE6FA",
  mid: "#E23AD2",
  rim: "#2E0A2C",
};

// ── Reduced-motion (BL-45) ───────────────────────────────────────────────────
// The cloak's reduced-motion read (S82 groundwork) is now folded into the shared
// MotionSettings.isReducedMotion() (system pref + in-game toggle) — one cached
// read for the whole renderer. Render-only, never touches sim → determinism-safe.

/** Lay down the phantom body path centred at (cx, cy) at the given radius. */
function pathPhantom(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.beginPath();
  buildPhantom(ctx, cx, cy, r);
}

// ── UNDETECTED STATE (cloak) ─────────────────────────────────────────────────

/**
 * The cloak: a deep #C42DBE phantom you "can't quite lock onto" — low-alpha
 * emissive volume + alpha-flicker dropout + warm/cool chromatic fringe + a
 * jittering glitch edge, all wrapped in a heat-haze distortion.
 *
 * `transition` is the 0..1 re-cloak phase-in (was-exposed → hiding) from
 * `getTransitionProgress`, or -1 when settled; it eases the cloak back in.
 *
 * BL-88 (S115-F11): `hidden` (= `isStealthHidden(enemy)`) is now passed IN by
 * the caller — the renderer no longer imports the `systems` predicate (so the
 * `rendering→systems` value edge is gone). Drives only the untargetable marker.
 */
export function drawStealthGhost(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  ex: number,
  ey: number,
  r: number,
  time: number,
  transition = -1,
  hidden = false,
): void {
  ctx.save();

  const rm = isReducedMotion();
  const motion = rm ? 0.35 : 1; // global motion-amplitude damp
  // Re-cloaking (hot form dissolving back to cloak) eases the whole cloak in.
  const cloakIn = transition >= 0 ? settle(transition) : 1;

  // ── alpha-flicker dropout (the "hard to focus" read; damped if reduced) ──
  const baseAlpha =
    (0.2 + Math.sin(time * 3.1 + enemy.y * 0.05) * 0.07 * motion) * cloakIn;
  const dropout = !rm && flicker(time + enemy.x * 0.1, 0.07);
  const bodyAlpha = dropout ? 0.05 * cloakIn : baseAlpha;

  // ── heat-haze floor: displaced shimmer copies + wobbling refraction ring ──
  drawCloakHaze(ctx, ex, ey, r, time, motion, cloakIn);

  // ── chromatic fringe: warm + cool ghosts of the phantom around the body ──
  const aberr = (Math.sin(time * 1.9) * 2.2 + Math.cos(time * 3.1) * 0.9) * motion;
  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = bodyAlpha * 0.7;
  ctx.fillStyle = "#FF3BD6"; // warm fringe
  pathPhantom(ctx, ex - aberr, ey - aberr * 0.35, r);
  ctx.fill();
  ctx.fillStyle = "#4DE0FF"; // cool fringe
  pathPhantom(ctx, ex + aberr, ey + aberr * 0.35, r);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";

  // ── body: emissive #C42DBE phantom — a lit volume, but cloaked (low alpha) ──
  ctx.globalAlpha = bodyAlpha;
  ctx.fillStyle = emissiveFill(ctx, ex, ey, r, CLOAK_RAMP);
  pathPhantom(ctx, ex, ey, r);
  ctx.fill();

  // ── faint magenta glow (feeds bloom softly) ──
  ctx.globalAlpha = (0.12 + breathe(time, 0.5) * 0.08 * motion) * cloakIn;
  glowCache.draw(ctx, STEALTH_MAGENTA, r * 2.0, ex, ey, 0.2);

  // ── glitch edge: jittered phantom outline ──
  ctx.globalAlpha = (dropout ? 0.04 : 0.22) * cloakIn;
  ctx.strokeStyle = "#E66BE0";
  ctx.lineWidth = 0.7;
  const jit = Math.sin(time * 15.7 + enemy.x) * 1.3 * motion;
  pathPhantom(ctx, ex + jit, ey, r);
  ctx.stroke();

  if (hidden) {
    drawUntargetableMarker(ctx, ex, ey, r, time, motion);
  }

  ctx.restore();
}

/**
 * Heat-haze distortion (Canvas2D floor): a couple of displaced, scaled,
 * translucent copies of the phantom plus a wobbling "lens" ring — reads as
 * "the air bends here." Cheap (a few additive fills + one stroke), scales to a
 * full wave of cloaked daemons. The true WebGL refraction (warp what's behind)
 * is the shared BL-49/BL-50 follow-up.
 */
function drawCloakHaze(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
  motion: number,
  cloakIn: number,
): void {
  ctx.globalCompositeOperation = "lighter";
  const layers = 2;
  for (let i = 0; i < layers; i++) {
    const phase = time * (1.3 + i * 0.7) + i * 2.1;
    const warpX = Math.sin(phase) * (1.6 + i * 0.8) * motion;
    const warpY = Math.cos(phase * 0.8) * (1.0 + i * 0.5) * motion;
    const scale = 1.12 + i * 0.08; // slight overscan per layer
    ctx.globalAlpha = (0.05 - i * 0.015) * cloakIn * (0.5 + motion * 0.5);
    ctx.fillStyle = STEALTH_MAGENTA;
    pathPhantom(ctx, ex + warpX, ey + warpY, r * scale);
    ctx.fill();
  }

  // Wobbling refraction ring — the "lens" edge of the cloak field.
  ctx.globalAlpha = 0.1 * cloakIn * (0.4 + motion * 0.6);
  ctx.strokeStyle = STEALTH_MAGENTA;
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  const segs = 18;
  for (let s = 0; s <= segs; s++) {
    const a = (s / segs) * Math.PI * 2;
    const wob =
      1 +
      Math.sin(a * 3 + time * 2.5) * 0.06 * motion +
      Math.sin(a * 5 - time * 1.7) * 0.04 * motion;
    const rad = r * 1.35 * wob;
    const x = ex + Math.cos(a) * rad;
    const y = ey + Math.sin(a) * rad * 1.05;
    if (s === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.globalCompositeOperation = "source-over";
}

/** The "can't lock on" tell — a rotating dashed lock-ring + crossed-out reticle,
 *  restyled to the magenta cloak language. Gameplay-facing only (sim-side the
 *  enemy is already untargetable). */
function drawUntargetableMarker(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
  motion: number,
): void {
  ctx.globalAlpha = 0.4 + breathe(time, 1.3) * 0.16 * motion;
  ctx.strokeStyle = "#E9A9E4";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.lineDashOffset = -time * 12 * motion;
  ctx.beginPath();
  ctx.arc(ex, ey, r + 8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;

  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(ex - (r + 3), ey + (r + 3));
  ctx.lineTo(ex + (r + 3), ey - (r + 3));
  ctx.stroke();

  ctx.globalAlpha = 0.5 + breathe(time + 0.2, 1.7) * 0.18 * motion;
  ctx.fillStyle = "#EBC4E8";
  ctx.font = canvasFont("fieldLabel");
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("UNTARGETABLE", ex, ey + r + 10);
  ctx.textAlign = "start";
}

// ── DETECTED STATE (reveal + exposed) ────────────────────────────────────────

/**
 * The reveal/exposed form. When `transition` is 0..1 (just detected) it plays
 * the **white-hot phase-in**: the phantom snaps in (overshoot via `snap`) inside
 * an explosive near-white core that blows out and eases away (`settle`, feeding
 * the WebGL bloom), settling into a sharp, hot EXPOSED phantom under a red
 * system-alarm. `transition = -1` (settled) draws the stable exposed form.
 */
export function drawStealthRevealed(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  ex: number,
  ey: number,
  r: number,
  time: number,
  transition = -1,
): void {
  ctx.save();

  const rm = isReducedMotion();
  const motion = rm ? 0.35 : 1;
  const revealing = transition >= 0;
  // White-hot blowout: max at the reveal instant, eases out (no flash if reduced).
  const heat = revealing ? Math.max(0, 1 - settle(transition)) * (rm ? 0.4 : 1) : 0;
  // Scale pop: snap overshoot from a compressed phantom to rest (none if reduced).
  const popScale = revealing && !rm ? 0.45 + 0.55 * snap(transition) : 1;
  const rr = r * popScale;
  const alarm = 1 - heat; // alert chrome fades up as the white-hot fades out

  // ── hot magenta glow (feeds bloom), intensified by the reveal heat ──
  const glowPulse = 0.32 + breathe(time, 1.8) * 0.18 * motion + heat * 0.5;
  glowCache.draw(
    ctx,
    STEALTH_MAGENTA,
    rr * (2.0 + heat * 0.8),
    ex,
    ey,
    Math.min(1, glowPulse),
  );

  // ── EXPOSED body: hot emissive phantom (#C42DBE energised) ──
  ctx.globalAlpha = 0.92;
  ctx.fillStyle = emissiveFill(ctx, ex, ey, rr, EXPOSED_RAMP);
  pathPhantom(ctx, ex, ey, rr);
  ctx.fill();

  // ── sharp inner edge (white) — phantom rebuilt at 0.6 scale ──
  ctx.globalAlpha = 1;
  ctx.strokeStyle = "rgba(255,255,255,0.7)";
  ctx.lineWidth = 1.1;
  pathPhantom(ctx, ex, ey, rr * 0.6);
  ctx.stroke();

  // ── white-hot materialisation (the hammer) — feeds the WebGL bloom ──
  if (heat > 0.001) {
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = heat;
    ctx.fillStyle = "#FFFFFF";
    pathPhantom(ctx, ex, ey, rr * (0.6 + heat * 0.5));
    ctx.fill();
    ctx.globalAlpha = heat * 0.9;
    glowCache.draw(ctx, "#FFE9FB", rr * (1.6 + heat), ex, ey, heat);
    ctx.globalCompositeOperation = "source-over";

    // Shockwave ring snapping outward from the reveal point.
    ctx.globalAlpha = heat * 0.8;
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 2.4 * heat + 0.6;
    ctx.beginPath();
    ctx.arc(ex, ey, rr + snap(transition) * 26, 0, Math.PI * 2);
    ctx.stroke();
  }

  // ── alert pulse rings (ORACLE_RED system alarm), fading up post-flash ──
  ctx.strokeStyle = COLORS.ORACLE_RED;
  ctx.lineWidth = 1.4;
  const pulse1 = (time * 1.5) % 1.0;
  ctx.globalAlpha = (1 - pulse1) * 0.55 * alarm;
  ctx.beginPath();
  ctx.arc(ex, ey, rr + 3 + pulse1 * 12, 0, Math.PI * 2);
  ctx.stroke();
  const pulse2 = (time * 1.5 + 0.5) % 1.0;
  ctx.globalAlpha = (1 - pulse2) * 0.35 * alarm;
  ctx.beginPath();
  ctx.arc(ex, ey, rr + 3 + pulse2 * 12, 0, Math.PI * 2);
  ctx.stroke();

  // ── cycling BREACH/EXPOSED label (red alarm) ──
  const labelIdx = Math.floor(time * 1.2) % BREACH_LABELS.length;
  ctx.globalAlpha = (0.55 + breathe(time, 3.0) * 0.4 * motion) * alarm;
  ctx.fillStyle = COLORS.ORACLE_RED;
  ctx.font = canvasFont("fieldLabel");
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.shadowColor = COLORS.ORACLE_RED;
  ctx.shadowBlur = 4;
  ctx.fillText(BREACH_LABELS[labelIdx]!, ex, ey - rr - 6);
  ctx.shadowBlur = 0;
  ctx.textAlign = "start";

  // ── binary debris (more energetic during the reveal) ──
  drawDebris(ctx, enemy, ex, ey, rr, time, 0.7 + heat * 0.6, motion);

  ctx.restore();
}

/** Falling binary debris orbiting an exposed enemy (deterministic per-enemy
 *  seed; `intensity` rises during the reveal, `motion` damps under reduced). */
function drawDebris(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  ex: number,
  ey: number,
  r: number,
  time: number,
  intensity: number,
  motion: number,
): void {
  ctx.font = canvasFont("fieldNoise");
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = STEALTH_MAGENTA;
  const count = 5;
  for (let i = 0; i < count; i++) {
    const seed = enemy.x * 7.31 + enemy.y * 3.17 + i * 13.37;
    const angle = (seed + time * (1.5 + i * 0.3) * motion) % (Math.PI * 2);
    const dist = r + 4 + ((seed + time * 20 * motion) % 14);
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist + ((time * 30 * motion + seed) % 10);
    const alpha = 1.0 - ((seed + time * 20 * motion) % 14) / 14;
    ctx.globalAlpha = alpha * intensity;
    const charIdx = Math.floor(seed + time * 5 * motion) % DEBRIS_CHARS.length;
    ctx.fillText(DEBRIS_CHARS[charIdx]!, ex + dx, ey + dy);
  }
  ctx.globalAlpha = 1;
  ctx.textAlign = "start";
}

// ── TRANSITION TIMING ────────────────────────────────────────────────────────

/**
 * Reveal/cloak phase-in progress 0..1 (0 = just transitioned, 1 = settled), or
 * -1 if no transition is active. Read-only on `enemy.revealedAt` (sim-written by
 * BeamCombat) — this renderer never writes it. `time` is the render frame time
 * (seconds, same `getSimTime()` clock that stamped `revealedAt`).
 */
export function getTransitionProgress(enemy: Enemy, time: number): number {
  if (enemy.revealedAt <= 0) return -1;
  const elapsed = time - enemy.revealedAt;
  if (elapsed > TRANSITION_DURATION) return -1;
  return Math.min(elapsed / TRANSITION_DURATION, 1);
}
